import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Loader from "components/Loader";
import "./search-input.css";

export default function SearchInput({
   name,
   value,
   placeholder,
   style,
   useOptionsHook = () => ({ d: [], loading: false }),
   onChange
}) {
   
   const box_ref = useRef();
   const options_box_ref = useRef();
   const [ s, setS ] = useState( "" );
   const [ open, setOpen ] = useState( false );

   const { d, loading } = useOptionsHook( s );
   const options = d || [];

   const onSearchTxtChange = useMemo(() => {
      let timeout;

      return e => {
         clearTimeout( timeout );
         timeout = setTimeout(() => {
            setS( e.target.value );
         }, 450 );
      }
   }, []);

   useEffect(() => {
		
		const checkDocumentClick = e => {
			const dropdown = box_ref.current;
			if ( !dropdown ) return;
			( !options_box_ref.current?.contains( e.target ) && !dropdown.contains( e.target )) && setOpen( false );
		};

		window.document.addEventListener( "click", checkDocumentClick );
		return () => window.document.removeEventListener( "click", checkDocumentClick );
	}, []);

   useEffect(() => {

		setTimeout(() => {

			if ( !box_ref.current || !options_box_ref.current ) return;

			const dropdown_rect = box_ref.current?.getBoundingClientRect();
			const options_box = options_box_ref.current;
			
			options_box.style.top = dropdown_rect.height + "px";
			options_box.style.width = dropdown_rect.width + "px";
			options_box.style.display = "block";
		}, 60 )
	}, [ open ]);

   return (
      <>
         <div
            ref={ box_ref } 
            className="search-input"
            style={ style }
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={placeholder || "Wyszukaj"}
            tabIndex={0}
            onClick={() => {
               setOpen( o => !o )
            }}
            onKeyDown={(e) => {
               if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen( o => !o );
               } else if (e.key === 'Escape' && open) {
                  e.preventDefault();
                  setOpen(false);
               }
            }}
         >
            <svg className="fill" width="21" height="21" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
               <path d="M10.3047 0.211914C15.4825 0.474576 19.5995 4.75599 19.5996 9.99902L19.5869 10.5039C19.4776 12.6579 18.6707 14.6265 17.3906 16.1934C17.4041 16.2032 17.4176 16.213 17.4307 16.2236L17.5068 16.292L20.3066 19.0918L20.376 19.168C20.6961 19.5607 20.6727 20.1398 20.3066 20.5059C19.9406 20.8719 19.3615 20.8953 18.9688 20.5752L18.8926 20.5059L16.0928 17.7061L16.0244 17.6299C16.0136 17.6166 16.0042 17.6025 15.9941 17.5889C14.3056 18.9685 12.1505 19.7988 9.7998 19.7988L9.2959 19.7861C4.28495 19.5322 0.26689 15.5148 0.0126953 10.5039L0 9.99902C0.000104109 4.58679 4.38757 0.199323 9.7998 0.199219L10.3047 0.211914ZM9.7998 2.19922C5.49214 2.19932 2.0001 5.69136 2 9.99902C2 14.3068 5.49207 17.7987 9.7998 17.7988C14.1076 17.7988 17.5996 14.3068 17.5996 9.99902C17.5995 5.69129 14.1076 2.19922 9.7998 2.19922Z"/>
            </svg>

            { open && 
               <input 
                  autoFocus
                  name="s" 
                  defaultValue={ s }
                  placeholder={ placeholder }
                  onChange={ onSearchTxtChange }
                  aria-label={placeholder || "Wyszukaj"}
                  role="searchbox"
                  onKeyDown={(e) => {
                     if (e.key === 'Escape') {
                        e.preventDefault();
                        setOpen(false);
                        box_ref.current?.focus();
                     }
                     e.stopPropagation();
                  }}
               />
            }

            { !open && 
               <span style={{ color: !value ? "#6B6B6B" : "", pointerEvents: "none", userSelect: "none" }}>
                  { value?.label || value || placeholder }
               </span>
            }

            { !open && value && 
                <span 
                   style={{ marginLeft: "auto", cursor: "pointer" }}
                   role="button"
                   aria-label="Wyczyść wybór"
                   tabIndex={0}
                   onClick={ e => {
                      e.stopPropagation();
                      setS( "" );
                      onChange( name, undefined );
                   }}
                   onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                         e.preventDefault();
                         e.stopPropagation();
                         setS( "" );
                         onChange( name, undefined );
                      }
                   }}
                > 
                  <svg className="stroke" width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                     <path d="M1 1.5L13 13.5" strokeWidth="2" strokeLinecap="round"/>
                     <path d="M13 1.5L1 13.5" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
               </span>
            }
         </div>

         { open && createPortal(
            <div 
               ref={ options_box_ref } 
               className="search-input-options"
               role="listbox"
               aria-label="Opcje wyszukiwania"
            >
               { loading && <Loader/> }

               { !loading && options && !options.length && <div role="status"> Brak wyników </div> }

               { !loading && options && options?.map( opt => (
                  <div 
                     key={ opt.value }
                     role="option"
                     tabIndex={0}
                     onClick={( e ) => {
                        e.stopPropagation();
                        onChange( name, opt );
                        setS( "" );
                        setOpen( false );
                        box_ref.current?.focus();
                     }}
                     onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                           e.preventDefault();
                           onChange( name, opt );
                           setS( "" );
                           setOpen( false );
                           box_ref.current?.focus();
                        } else if (e.key === 'Escape') {
                           e.preventDefault();
                           setOpen(false);
                           box_ref.current?.focus();
                        }
                     }}
                  > 
                     { opt.label } 
                  </div>
               ))}
            </div>
         , box_ref.current )}
      </>
   )
}